import { api } from "@ancademy/vse-client";
import { IGame, IReport, TReportData } from "@ancademy/vse-share";
import { Md, Style } from "@client";
import { subViewList } from "@sub";
import { Button, DatePicker, Divider, Form, Input, InputNumber, message, Modal } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { ICreateParams } from "../../config";

export enum ReportItemType {
  text,
  textArea,
  number,
  date,
}

export type TReportConfig = Array<{
  label: string;
  type: ReportItemType;
}>;

export const DefaultReportConfig: TReportConfig = [
  {
    label: "指导老师",
    type: ReportItemType.text,
  },
  {
    label: "实验时间",
    type: ReportItemType.date,
  },
  {
    label: "一、实验项目名称",
    type: ReportItemType.text,
  },
  {
    label: "二、实验学时",
    type: ReportItemType.number,
  },
  {
    label: "三、实验原理",
    type: ReportItemType.textArea,
  },
  {
    label: "四、实验目的",
    type: ReportItemType.textArea,
  },
  {
    label: "五、实验内容",
    type: ReportItemType.textArea,
  },
  {
    label: "六、实验步骤",
    type: ReportItemType.textArea,
  },
  {
    label: "七、实验数据及结果分析",
    type: ReportItemType.textArea,
  },
  {
    label: "八、实验结论",
    type: ReportItemType.textArea,
  },
  {
    label: "九、总结及心得体会",
    type: ReportItemType.textArea,
  },
  {
    label: "十、对本实验过程及方法、手段的改进建议",
    type: ReportItemType.textArea,
  },
];

export function ReportModal({
  gameId,
  reportConfig = DefaultReportConfig,
  children,
  allSubGameOver,
}: {
  gameId: string;
  allSubGameOver: boolean;
  reportConfig?: TReportConfig;
  children?: React.ReactNode;
}) {
  const defaultData: TReportData = {};
  reportConfig.forEach(({ type }, i) => {
    let value = null;
    switch (type) {
      case ReportItemType.text:
      case ReportItemType.textArea:
        value = "";
        break;
      case ReportItemType.date:
        value = moment().format(moment.HTML5_FMT.DATE);
        break;
    }
    defaultData[i] = value;
  });
  const PageSize = 3;
  const [page, setPage] = useState(0);
  const [visible, setVisible] = useState(false);
  const [report, setReport] = useState(null as IReport);
  const [data, setData] = useState(defaultData);
  useEffect(() => {
    api.getMyReport(gameId).then((report) => {
      if (!report) {
        return;
      }
      const { data } = report;
      setReport(report);
      setData(data);
    });
  }, []);
  return (
    <>
      <div onClick={() => setVisible(true)}>{children}</div>
      <Modal
        {...Md.pageProps}
        title={"实验报告"}
        visible={visible}
        okText="保存"
        footer={[
          <Button className={Style.btnWarningSm} onClick={() => setVisible(false)}>
            收起
          </Button>,
          ...(report?.submitted
            ? []
            : [
                <Button
                  className={Style.btnPrimarySm}
                  onClick={async () => {
                    const report = await api.saveReport({
                      gameId,
                      data,
                    });
                    message.success("保存成功!");
                    setReport(report);
                    setVisible(false);
                  }}
                >
                  保存
                </Button>,
                <Button
                  className={Style.btnPrimarySm}
                  onClick={() => {
                    if (allSubGameOver) {
                      const modal = Modal.confirm({
                        width: Md.fnWidth,
                        centered: true,
                        content: "注意：报告提交后将不可再修改。",
                        cancelText: "取消",
                        okText: "提交",
                        onOk: async () => {
                          const report = await api.saveReport({
                            gameId,
                            data,
                          });
                          const k = subViewList.length;
                          const {
                              params: { behaviorScore, questionScore },
                            }: IGame<ICreateParams> = await api.getMainGame(gameId),
                            score = ~~(100 - (~~(behaviorScore / k) + ~~(questionScore / k)) * k);
                          const result = await api.getMyResult(gameId);
                          await api.saveStepResult({
                            id: result.id,
                            stepResult: {
                              seq: k + 1,
                              score,
                              maxScore: score,
                              title: "实验报告",
                              expectTime: 180,
                            },
                            stepAmount: k + 1,
                          });
                          await api.submitReport(report.id);
                          modal.destroy();
                          setVisible(false);
                          message.success("实验报告提交成功！", 1, () => location.reload());
                        },
                      });
                    } else {
                      Modal.warning({
                        centered: true,
                        content: "请先完成所有实验环节。",
                      });
                    }
                  }}
                >
                  提交报告
                </Button>,
              ]),
        ]}
      >
        <Form style={{ padding: "1rem 2rem" }} labelCol={{ span: "24" }}>
          {reportConfig.map(({ label, type = ReportItemType.textArea }, i) => {
            const val = data[i],
              setValue = (val) => {
                const _data = { ...data };
                _data[i] = val;
                setData(_data);
              };
            if (i < page * PageSize || i >= (page + 1) * PageSize) {
              return null;
            }
            return (
              <Form.Item label={label} key={label}>
                {(() => {
                  switch (type) {
                    case ReportItemType.text:
                      return (
                        <Input maxLength={100} value={val} onChange={({ target: { value } }) => setValue(value)} />
                      );
                    case ReportItemType.textArea:
                      return (
                        <Input.TextArea
                          rows={6}
                          maxLength={2000}
                          value={val}
                          onChange={({ target: { value } }) => setValue(value)}
                        />
                      );
                    case ReportItemType.number:
                      return <InputNumber value={val as number} onChange={(value) => setValue(value)} />;
                    case ReportItemType.date:
                      return (
                        <DatePicker
                          value={moment(val)}
                          onChange={(val) => setValue(val.format(moment.HTML5_FMT.DATE))}
                        />
                      );
                  }
                })()}
              </Form.Item>
            );
          })}
        </Form>
        <div style={{ textAlign: "center" }}>
          <Button type="link" disabled={page <= 0} onClick={() => setPage(page - 1)}>
            上一页
          </Button>
          <Divider type="vertical" />
          <Button type="link" disabled={(page + 1) * PageSize >= reportConfig.length} onClick={() => setPage(page + 1)}>
            下一页
          </Button>
        </div>
      </Modal>
    </>
  );
}
